<template>
  <loading-placeholder v-if="isLoading" :lines="maxRows" class="add-people-form__loading" />
  <div v-else class="add-guest-form">
    <el-form v-if="formData.listInvitees.length > 0" ref="form" class="add-people-form__form">
      <div v-for="(item, index) in formData.listInvitees.length" :key="index" class="add-people-form__form--row">
        <el-form-item :class="hasMultipleCompanies ? 'col-md-5' : 'col-md-8'">
          <standard-email-input
            v-model="formData.listInvitees[index].email"
            placeholder="Invite by email address..."
            validate-if-blank
            @invalid-on-blur="addInvalidEmailIndex(index)"
            @valid-on-blur="addValidEmailIndex(index)"
          />
        </el-form-item>
        <el-form-item v-show="hasMultipleCompanies" class="col-md-3">
          <single-select v-model="formData.listInvitees[index].companyID" placeholder="Company">
            <el-option v-for="item in companies" :key="item.id" :label="item.name" :value="item.id" />
          </single-select>
        </el-form-item>
        <el-form-item class="col-md-1">
          <dd-icon-button
            v-if="isSingleInvite"
            :is-disabled="isSubmitting"
            :class="AppSumoAssistant.getCreateAccountMembershipButtonMarkerClass()"
            class="add-single-person"
            icon="plus" 
            size="medium" 
            color="blue" 
            type="circle" 
            title="Invite user" 
            @click="createAccountMemberships" 
          />
        </el-form-item>
      </div>
      <dd-button v-if="!maxRowsReached" class="add-another" type="text" @click="addRow">+ Add Another</dd-button>
    </el-form>
    <dd-button
      v-if="!isSingleInvite"
      :is-loading="isSubmitting"
      size="large" 
      color="blue" 
      @click="createAccountMemberships">
      Send Invites
    </dd-button>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */

/* Helpers */
import AppSumoAssistant from '@/helpers/appsumo-assistant'

/* Data types */
import { AccountRoles } from '@/types/defined-types'
import {
  IAccountMembershipsInputCreate,
  IAccountMembershipsInputCreateInvitee,
} from '@/api/types/request.types'

/* API */
import AccountMembershipsAPI from '@/api/modules/account-memberships.api'
import { IItemForSelection } from '@/api/types/response.types'

export default defineComponent({
  name: 'AddGuestForm',

  props: {
    initialRows: {
      type: Number,
      default: 3,
    },
    maxRows: {
      type: Number,
      default: 10,
    },
    entityIdToAssign: {
      type: Number,
      default: null,
    },
    hideSuccessMessaging: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['account-memberships-created'],

  data() {
    return {
      AppSumoAssistant,
      isLoading: true,
      isSubmitting: false,
      AccountRoles,
      invalidEmailRows: [] as number[],
      validEmailRows: [] as number[],
      companies: [] as IItemForSelection[],
      formData: {
        sendInvitations: true,
        listInvitees: [] as IAccountMembershipsInputCreateInvitee[],
      } as IAccountMembershipsInputCreate,
    }
  },

  computed: {
    hasMultipleCompanies(): boolean {
      return this.companies.length > 1
    },
    lastInvitee(): IAccountMembershipsInputCreateInvitee {
      return this.formData.listInvitees[this.formData.listInvitees.length - 1]
    },
    isSingleInvite(): boolean {
      return this.maxRows === 1 && this.initialRows === 1
    },
    availableRoles(): IItemForSelection[] {
      return this.$permissions.availableRolesToAdmin
    },
    maxRowsReached(): boolean {
      return this.formData.listInvitees.length >= this.maxRows
    },
  },

  async created() {
    this.isLoading = true
    await this.getCreateOptions()
    this.resetFormData()
    this.isLoading = false
  },

  methods: {
    resetFormData() {
      let i
      this.formData.listInvitees = []
      for (i = 0; i < this.initialRows; i++) {
        this.formData.listInvitees.push({
          email: '',
          companyID: this.companies[0].id,
          accountRoleID: 4,
        })
      }
      if (this.entityIdToAssign) {
        this.formData.projectID = this.entityIdToAssign
      }
    },
    addRow(): void {
      /* Stub new row with blank email but carryover other columns */
      this.formData.listInvitees.push({
        email: '',
        accountRoleID: this.lastInvitee.accountRoleID,
        companyID: this.lastInvitee.companyID,
      })
    },
    addInvalidEmailIndex(index: number) {
      // Remove the index from the valid list...
      if (this.validEmailRows.includes(index)) {
        this.validEmailRows = this.validEmailRows.filter(e => e !== index)
      }

      // Add to the invalid list...
      if (this.invalidEmailRows.includes(index)) {
        return
      }
      this.invalidEmailRows.push(index)
    },
    addValidEmailIndex(index: number) {
      // Remove the index from the invalid list...
      if (this.invalidEmailRows.includes(index)) {
        this.invalidEmailRows = this.invalidEmailRows.filter(e => e !== index)
      }

      // Add to the valid list...
      if (this.validEmailRows.includes(index)) {
        return
      }
      this.validEmailRows.push(index)
    },
    async getCreateOptions() {
      try {
        const createOptions = await AccountMembershipsAPI.get_CreateOptions()
        this.companies = createOptions.listCompanies
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async createAccountMemberships() {
      /* Gotcha: For multi-invites, they may only have partial AM creations if they go over limit... */
      if (
        !(await AppSumoAssistant.isUnderAccountMembershipsLimit(
          this.$router,
          this.$permissions,
        ))
      ) {
        return
      }

      this.isSubmitting = true
      try {
        const response = await AccountMembershipsAPI.post_Create(this.formData)

        if (!this.hideSuccessMessaging) {
          this.$messaging.showSuccess(response.message)
        }
        this.resetFormData()
        this.$emit('account-memberships-created')
      } catch (error) {
        this.$error.emit(error, true)
      }
      this.isSubmitting = false
    },
  },
})
</script>

<style lang="scss">
.add-guest-form {
  &__loading {
    padding-top: 20px;
  }

  &__form {
    margin: 16px 0;

    .add-single-person {
      margin-left: auto;
    }

    .add-another {
      padding: 0;
    }

    &--row {
      display: flex;
      align-items: center;

      > .el-form-item {
        // Override bootstrap grid padding
        padding: 8px 15px 8px 0 !important;
        // Override el-form-item
        margin-bottom: 0 !important;

        &:last-child {
          padding-right: 0 !important;
        }
      }

      @include media-breakpoint-down(sm) {
        flex-direction: column;
        align-items: flex-start;

        > .el-form-item {
          width: 100%;
          padding: 0 0 20px 0 !important;
        }

        margin-bottom: 20px;
        border-bottom: $border--solid-extra-light;
      }
    }
  }
}
</style>
